/**
 * SubjectMembersModal — kto należy do zespołu albo ma tę rolę (F24 Kawałek 1).
 *
 * Podmiot bez ludzi nic nie robi, więc to okienko domyka pętlę: załóż rolę albo zespół → wrzuć
 * osoby → nadaj prawa w drzewie obok.
 *
 * Układ celowo dwudzielny: po lewej KTO JUŻ JEST (z przyciskiem wypisania), po prawej KOGO MOŻNA
 * DOPISAĆ (z wyszukiwarką i „dodaj wszystkich"). Lista „wszyscy z zaznaczeniem" gubiłaby
 * odpowiedź na pytanie „kto właściwie tu jest", a to pierwsze, po co się tu wchodzi.
 *
 * ── Zespół a rola: ta sama tabelka, inne znaczenie ───────────────────────────────────────────
 * ZESPÓŁ należy do jednej firmy (jak rola), a członkostwo to osobna lista — dopisanie
 * i wypisanie nie rusza niczego poza nią. Dopisać można tylko osobę z dostępem do firmy
 * zespołu (serwer odrzuca resztę), więc lista kandydatów pokazuje wyłącznie takie osoby.
 *
 * ROLA żyje w JEDNEJ firmie, a człowiek może mieć w firmie kilka ról (ADR-0103). „Dopisanie do
 * roli" DOKŁADA tę rolę na końcu listy ról osoby (pozostałe zostają; kto nie miał dostępu do
 * firmy, dostaje go przy okazji — rola bez dostępu nie istnieje). „Wypisanie" zabiera TYLKO tę
 * rolę — dostęp do firmy i inne role zostają, więc jest równie lekkie jak wypisanie z zespołu i
 * nie wymaga potwierdzenia. Odebranie całego dostępu robi się na karcie użytkownika (Firmy).
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Search, UserMinus, UserPlus } from 'lucide-react';

import { Button } from '@/components/Button';
import { IconButton } from '@/components/IconButton';
import { Input } from '@/components/Input';
import { Modal } from '@/components/Modal';
import { Spinner } from '@/components/Spinner';
import { useToast } from '@/contexts/ToastContext';
import { api } from '@/lib/api';
import { TEAMS_KEY, useUsersForCenter } from '@/lib/use-permission-center';

interface Member {
  userId: string;
  email: string;
  displayName: string | null;
}

interface CompanyMember extends Member {
  roles: Array<{ id: string; name: string }>;
}

export const TEAM_MEMBERS_KEY = (teamId: string) => ['teams', 'members', teamId] as const;
export const COMPANY_MEMBERS_KEY = (companyId: string) =>
  ['companies', 'members', companyId] as const;

export interface MembersSubject {
  kind: 'team' | 'role';
  id: string;
  name: string;
}

interface SubjectMembersModalProps {
  open: boolean;
  subject: MembersSubject | null;
  /** Firma, w której żyje rola albo zespół (oba są per firma). */
  companyId: string | null;
  /** Bez prawa zarządzania okienko jest do oglądania — bez dopisywania i wypisywania. */
  canManage: boolean;
  onClose: () => void;
}

export function SubjectMembersModal({
  open,
  subject,
  companyId,
  canManage,
  onClose,
}: SubjectMembersModalProps) {
  const { t } = useTranslation();
  const toast = useToast();
  const qc = useQueryClient();

  const [search, setSearch] = useState('');

  const isTeam = subject?.kind === 'team';
  const enabled = open && subject !== null && companyId !== null;

  const members = useQuery<{ members: Member[] }>({
    queryKey: TEAM_MEMBERS_KEY(subject?.id ?? 'none'),
    queryFn: async () =>
      (await api.get<{ members: Member[] }>(`/teams/${subject!.id}/members`)).data,
    enabled: enabled && isTeam,
  });

  // Skład firmy (kto ma dostęp + jakie role). Rola: z niego liczymy i „kto ma tę rolę", i „kogo
  // dopisanie przy okazji wpuści do firmy". Zespół: kandydaci to tylko osoby z dostępem.
  const companyMembers = useQuery<{ members: CompanyMember[] }>({
    queryKey: COMPANY_MEMBERS_KEY(companyId ?? 'none'),
    queryFn: async () =>
      (await api.get<{ members: CompanyMember[] }>(`/companies/${companyId}/members?limit=100`))
        .data,
    enabled,
  });

  const assigned: Member[] = useMemo(() => {
    if (isTeam) return members.data?.members ?? [];
    return (companyMembers.data?.members ?? []).filter((m) =>
      m.roles.some((r) => r.id === subject?.id),
    );
  }, [isTeam, members.data, companyMembers.data, subject?.id]);

  const withCompanyAccess = useMemo(
    () => new Set((companyMembers.data?.members ?? []).map((m) => m.userId)),
    [companyMembers.data],
  );
  const loading = isTeam ? members.isLoading || companyMembers.isLoading : companyMembers.isLoading;

  const users = useUsersForCenter();

  const refresh = async (): Promise<void> => {
    if (!subject) return;
    if (isTeam) {
      await qc.invalidateQueries({ queryKey: TEAM_MEMBERS_KEY(subject.id) });
      // Lista zespołów pokazuje licznik osób — bez tego zostałby nieaktualny.
      await qc.invalidateQueries({ queryKey: TEAMS_KEY });
      return;
    }
    await qc.invalidateQueries({ queryKey: COMPANY_MEMBERS_KEY(companyId!) });
    await qc.invalidateQueries({ queryKey: ['permissions', 'center', 'access'] });
  };

  const add = useMutation({
    mutationFn: async (userIds: string[]) => {
      if (isTeam) {
        await api.post(`/teams/${subject!.id}/members`, { userIds });
        return;
      }
      // Dokłada TĘ rolę (pozostałe role osoby zostają). Trasa bierze jedną osobę — przy „dodaj
      // wszystkich" idzie w pętli.
      for (const userId of userIds) {
        await api.put(`/companies/${companyId}/members/${userId}/roles/${subject!.id}`);
      }
    },
    onSuccess: refresh,
    onError: () => toast.show({ variant: 'error', title: t('tenant.teams.members.error') }),
  });

  const remove = useMutation({
    mutationFn: async (userId: string) => {
      if (isTeam) {
        await api.delete(`/teams/${subject!.id}/members/${userId}`);
        return;
      }
      // Zabiera tylko tę rolę — dostęp do firmy i pozostałe role zostają.
      await api.delete(`/companies/${companyId}/members/${userId}/roles/${subject!.id}`);
    },
    onSuccess: refresh,
    onError: () => toast.show({ variant: 'error', title: t('tenant.teams.members.error') }),
  });

  const memberIds = useMemo(() => new Set(assigned.map((m) => m.userId)), [assigned]);

  const candidates = useMemo(() => {
    const needle = search.trim().toLowerCase();
    return (
      (users.data?.users ?? [])
        .filter((u) => !memberIds.has(u.id))
        // Zespół: tylko osoby z dostępem do firmy zespołu — innych serwer nie przyjmie.
        .filter((u) => !isTeam || withCompanyAccess.has(u.id))
        .filter(
          (u) =>
            needle === '' ||
            (u.displayName ?? '').toLowerCase().includes(needle) ||
            u.email.toLowerCase().includes(needle),
        )
    );
  }, [users.data, memberIds, search, isTeam, withCompanyAccess]);

  const busy = add.isPending || remove.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={t(`tenant.teams.members.title.${subject?.kind ?? 'team'}`, {
        name: subject?.name ?? '',
      })}
      description={t(`tenant.teams.members.hint.${subject?.kind ?? 'team'}`)}
      footer={
        <Button variant="secondary" onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {loading ? (
        <div className="flex justify-center p-6">
          <Spinner />
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2">
          <section className="flex min-w-0 flex-col gap-2">
            <h3 className="text-sm font-medium text-text-primary">
              {t('tenant.teams.members.inTeam', { count: assigned.length })}
            </h3>
            {assigned.length === 0 ? (
              <p className="text-sm text-text-muted">{t('tenant.teams.members.empty')}</p>
            ) : (
              <ul className="divide-y divide-border-primary/60 overflow-hidden rounded-lg border border-border-primary">
                {assigned.map((member) => (
                  <li key={member.userId} className="flex items-center gap-3 px-3.5 py-2.5">
                    <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                      <p className="truncate text-sm leading-tight text-text-primary">
                        {member.displayName ?? member.email}
                      </p>
                      {member.displayName && (
                        <p className="truncate text-xs leading-tight text-text-muted">
                          {member.email}
                        </p>
                      )}
                    </div>
                    {canManage && (
                      <IconButton
                        size="sm"
                        disabled={busy}
                        aria-label={t(`tenant.teams.members.remove.${subject?.kind ?? 'team'}`)}
                        title={t(`tenant.teams.members.remove.${subject?.kind ?? 'team'}`)}
                        onClick={() => remove.mutate(member.userId)}
                      >
                        <UserMinus size={14} aria-hidden="true" />
                      </IconButton>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </section>

          {canManage && (
            <section className="flex min-w-0 flex-col gap-2">
              <div className="flex items-center justify-between gap-2">
                <h3 className="text-sm font-medium text-text-primary">
                  {t('tenant.teams.members.available')}
                </h3>
                {/*
                    Przy zakładaniu zespołu „wszyscy" to najczęstszy pierwszy ruch, a klikanie po
                    jednym przy kilkudziesięciu kontach jest karą za nic.
                  */}
                <Button
                  variant="secondary"
                  size="sm"
                  disabled={busy || candidates.length === 0}
                  onClick={() => add.mutate(candidates.map((u) => u.id))}
                >
                  {t('tenant.teams.members.addAll', { count: candidates.length })}
                </Button>
              </div>
              <Input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder={t('tenant.teams.members.searchPlaceholder')}
                aria-label={t('tenant.teams.members.searchPlaceholder')}
                prefixIcon={<Search className="h-3.5 w-3.5" />}
              />
              {candidates.length === 0 ? (
                <p className="text-sm text-text-muted">{t('tenant.teams.members.noCandidates')}</p>
              ) : (
                <ul className="max-h-72 divide-y divide-border-primary/60 overflow-y-auto rounded-lg border border-border-primary">
                  {candidates.map((user) => (
                    <li key={user.id} className="flex items-center gap-3 px-3.5 py-2.5">
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        <p className="truncate text-sm leading-tight text-text-primary">
                          {user.displayName ?? user.email}
                        </p>
                        {user.displayName && (
                          <p className="truncate text-xs leading-tight text-text-muted">
                            {user.email}
                          </p>
                        )}
                        {!isTeam && !withCompanyAccess.has(user.id) && (
                          <p className="text-xs leading-tight text-text-muted">
                            {t('tenant.teams.members.grantsAccess')}
                          </p>
                        )}
                      </div>
                      <IconButton
                        size="sm"
                        disabled={busy}
                        aria-label={t('tenant.teams.members.add')}
                        title={t('tenant.teams.members.add')}
                        onClick={() => add.mutate([user.id])}
                      >
                        <UserPlus size={14} aria-hidden="true" />
                      </IconButton>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          )}
        </div>
      )}
    </Modal>
  );
}
